import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, ArrowLeft, Tag, Share2, Clock, Rss, Facebook, Twitter, Linkedin, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { SEO, getBreadcrumbSchema } from '../components/SEO';

const API = '/api';

// Placeholder image
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80';

export default function InsightDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, direction } = useLanguage();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInsight();
  }, [slug]);

  const fetchInsight = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}/insights/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Article not found');
        } else {
          setError('Failed to load article');
        }
        return;
      }
      const data = await response.json();
      setInsight(data);
    } catch (err) {
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', options);
  };

  const getReadingTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
    return Math.max(1, Math.ceil(words / wordsPerMinute));
  };

  const handleShare = async (platform) => {
    // Use production domain for social sharing URLs
    const productionDomain = 'https://www.wedohr.net';
    const articleSlug = slug;
    
    // Use og-preview endpoint for social platforms - it has proper OG meta tags
    // and redirects real users to the actual article page
    const ogPreviewUrl = `${productionDomain}/api/insights/${articleSlug}/og-preview`;
    const directUrl = `${productionDomain}/insights/${articleSlug}`;
    const title = language === 'ar' ? insight?.title_ar : insight?.title_en;
    
    switch (platform) {
      case 'facebook':
        // Facebook crawler follows redirects, so og-preview works well
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogPreviewUrl)}`, '_blank');
        break;
      case 'twitter':
        // Twitter/X also follows redirects
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(ogPreviewUrl)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        // LinkedIn crawler needs og-preview for proper meta tag parsing
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogPreviewUrl)}`, '_blank');
        break;
      case 'copy':
        // For manual copy, use the direct article URL
        try {
          await navigator.clipboard.writeText(directUrl);
          toast.success(language === 'ar' ? 'تم نسخ الرابط' : 'Link copied to clipboard');
        } catch {
          toast.error(language === 'ar' ? 'فشل نسخ الرابط' : 'Failed to copy link');
        }
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="h-12 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="aspect-[16/9] bg-gray-200 rounded-xl mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !insight) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {language === 'ar' ? 'المقال غير موجود' : 'Article Not Found'}
          </h1>
          <p className="text-gray-600 mb-8">
            {language === 'ar' 
              ? 'عذراً، المقال الذي تبحث عنه غير موجود'
              : 'Sorry, the article you are looking for does not exist'}
          </p>
          <Link to="/insights">
            <Button>
              <ArrowLeft className={`h-4 w-4 ${language === 'ar' ? 'ml-2 rotate-180' : 'mr-2'}`} />
              {language === 'ar' ? 'العودة للمقالات' : 'Back to Insights'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get content based on language
  const title = language === 'ar' ? insight.title_ar : insight.title_en;
  const content = language === 'ar' ? insight.content_ar : insight.content_en;
  const excerpt = language === 'ar' ? insight.excerpt_ar : insight.excerpt_en;
  const seoTitle = language === 'ar' ? insight.seo_title_ar : insight.seo_title_en;
  const metaDescription = language === 'ar' ? insight.meta_description_ar : insight.meta_description_en;
  const readingTime = getReadingTime(content);

  // Breadcrumbs
  const breadcrumbs = [
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: 'https://www.wedohr.net' },
    { name: language === 'ar' ? 'رؤى ويدو' : 'Insights', url: 'https://www.wedohr.net/insights' },
    { name: title, url: `https://www.wedohr.net/insights/${insight.slug}` }
  ];

  // Article Schema for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": excerpt,
    "image": insight.featured_image || PLACEHOLDER_IMAGE,
    "datePublished": insight.publish_date,
    "dateModified": insight.updated_at || insight.publish_date,
    "author": {
      "@type": "Organization",
      "name": "WeDo HR Services",
      "url": "https://www.wedohr.net"
    },
    "publisher": {
      "@type": "Organization",
      "name": "WeDo HR Services",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.wedohr.net/insights/${insight.slug}`
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" dir={direction}>
      <SEO 
        title={seoTitle || title}
        description={metaDescription || excerpt}
        canonicalUrl={`/insights/${insight.slug}`}
        ogType="article"
        ogImage={insight.featured_image || PLACEHOLDER_IMAGE}
        structuredData={articleSchema}
        additionalSchemas={[getBreadcrumbSchema(breadcrumbs)]}
      />

      {/* Hero / Header */}
      <article>
        <header className="bg-white border-b">
          <div className="container-custom py-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 max-w-4xl mx-auto flex-wrap">
              <Link to="/" className="hover:text-[#33AADD] shrink-0">
                {language === 'ar' ? 'الرئيسية' : 'Home'}
              </Link>
              <span className="shrink-0">/</span>
              <Link to="/insights" className="hover:text-[#33AADD] shrink-0">
                {language === 'ar' ? 'رؤى ويدو' : 'Insights'}
              </Link>
              <span className="shrink-0">/</span>
              <span className="text-gray-700 truncate max-w-[200px] sm:max-w-xs">{title}</span>
            </nav>

            <div className="max-w-4xl mx-auto">
              {/* Category & Reading Time */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                {insight.category && (
                  <Badge className="bg-[#33AADD] hover:bg-[#2890BD] text-white">
                    {insight.category}
                  </Badge>
                )}
                <span className="flex items-center gap-1 text-gray-500 text-sm">
                  <Clock className="h-4 w-4" />
                  {language === 'ar' 
                    ? `${readingTime} دقيقة قراءة`
                    : `${readingTime} min read`}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-4 leading-tight break-words">
                {title}
              </h1>

              {/* Date & Share */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-gray-500">
                  <Calendar className="h-5 w-5" />
                  <span>{formatDate(insight.publish_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 mr-2">
                    {language === 'ar' ? 'مشاركة:' : 'Share:'}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleShare('linkedin')}
                  >
                    <Linkedin className="h-4 w-4 text-[#0077b5]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleShare('twitter')}
                  >
                    <Twitter className="h-4 w-4 text-[#1da1f2]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleShare('facebook')}
                  >
                    <Facebook className="h-4 w-4 text-[#1877f2]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleShare('copy')}
                  >
                    <LinkIcon className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="bg-white pb-8">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <img 
                src={insight.featured_image || PLACEHOLDER_IMAGE}
                alt={title}
                className="w-full aspect-[16/9] object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="py-12">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div 
                className="prose prose-lg prose-gray max-w-none 
                  prose-headings:text-[#111827] prose-headings:font-bold
                  prose-p:text-gray-700 prose-p:leading-relaxed
                  prose-a:text-[#33AADD] prose-a:no-underline hover:prose-a:underline 
                  prose-img:rounded-xl prose-img:max-w-full prose-img:h-auto
                  prose-pre:overflow-x-auto prose-pre:max-w-full
                  prose-table:overflow-x-auto
                  [&_*]:max-w-full"
                style={{ 
                  direction,
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{ __html: content }}
              />

              {/* Tags */}
              {insight.tags && insight.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t">
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag className="h-5 w-5 text-gray-400" />
                    {insight.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="bg-gray-100 text-gray-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Back to Insights */}
              <div className="mt-12 pt-8 border-t">
                <Link to="/insights">
                  <Button variant="outline" className="group">
                    <ArrowLeft className={`h-4 w-4 group-hover:-translate-x-1 transition-transform ${language === 'ar' ? 'ml-2 rotate-180 group-hover:translate-x-1' : 'mr-2'}`} />
                    {language === 'ar' ? 'العودة للمقالات' : 'Back to Insights'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#33AADD] to-[#2890BD]">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {language === 'ar' 
              ? 'هل تحتاج مساعدة في الموارد البشرية أو التوظيف؟'
              : 'Need help with HR or recruitment?'}
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            {language === 'ar'
              ? 'تواصل معنا لمناقشة احتياجاتك مع فريق الخبراء لدينا'
              : 'Get in touch with our expert team to discuss your needs'}
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-gray-100">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
